import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUserOrganizations,
  useAdminUserErasure,
  useAppAdmins,
  useDeleteAdminUserProviderMutation,
  useGrantAppAdminMutation,
  useRevokeAppAdminMutation,
  useSetAdminUserStatusMutation,
} from '@reflex/ui/client/api/admin';
import {
  Building2,
  Ban,
  CheckCircle2,
  Clock,
  Mail,
  ShieldUser,
  ShieldCheck,
  Unlink,
  User as UserIcon,
} from 'lucide-react';
import { Suspense, useState } from 'react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Switch } from '@reflex/ui/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { toast } from 'sonner';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { ErrorState } from '@reflex/ui/components/shared/ErrorState';
import { useAuth } from '@/hooks/auth/useAuth';
import { useUser } from '@/hooks/auth/useUser';
import { useUserProviders } from '@/hooks/auth/useUserProviders';
import { usePlugins } from '@/hooks/config/usePlugins';
import type { AccountStatus, UserLifecycleStatus } from '@/lib/types';
import { queryKeys } from '@/lib/query-keys';
import { formatDateLong, getProviderLabel, getProviderName } from '@reflex/ui/lib/format';
import { getUserDisplayName } from '@/lib/agent-utils';
import { getAuthProviderIcon } from '@/lib/plugin-registry';
import { ROUTES } from '@/lib/routes';

import { UserErasureSection } from './UserErasureSection';

type LinkedProvider = {
  id: string;
  provider: string;
  providerUserId: string;
  providerData: Record<string, unknown> | null;
  createdAt: number;
};

interface ProvidersSectionProps {
  userId: string;
  providers: LinkedProvider[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

function ProvidersSection({ userId, providers, isLoading, isError }: ProvidersSectionProps) {
  const queryClient = useQueryClient();

  // Admin override: no "last provider" guard — unlinking can leave the
  // user unable to sign in, which is the intended operator capability.
  const unlinkMutation = useDeleteAdminUserProviderMutation();
  const unlink = (provider: string) =>
    unlinkMutation.mutate(
      { userId, provider },
      {
        onSuccess: () => {
          toast.success('Provider unlinked');
          void queryClient.invalidateQueries({ queryKey: queryKeys.userProvidersById(userId) });
        },
        onError: (err) => {
          toast.error('Failed to unlink provider', {
            description: err instanceof Error ? err.message : undefined,
          });
        },
      },
    );

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message="Failed to load linked providers." />;
  if (!providers || providers.length === 0) {
    return (
      <EmptyState
        variant="bordered"
        title="No linked OAuth providers"
        description="This user has not connected any OAuth providers."
      />
    );
  }

  const sorted = [...providers].sort((a, b) => a.provider.localeCompare(b.provider));

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((account) => {
        const label = getProviderName(account.provider);
        const Icon = getAuthProviderIcon(account.provider);
        const displayLogin =
          (account.providerData as { login?: string } | null)?.login ?? account.providerUserId;
        return (
          <div
            key={account.id}
            data-testid={`linked-provider-${account.provider}`}
            className="flex flex-col gap-3 rounded-lg border p-3 min-[440px]:flex-row min-[440px]:items-center min-[440px]:justify-between"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                {Icon ? (
                  <Icon className="h-4 w-4" />
                ) : (
                  <img
                    src={`/plugin-icons/plugin-icon-auth-${account.provider}.png`}
                    alt={label}
                    className="h-5 w-5 object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">{label}</span>
                  <Badge variant="secondary" className="gap-1 text-3xs">
                    <CheckCircle2 className="h-3 w-3 text-status-success" />
                    Linked
                  </Badge>
                </div>
                <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                  {displayLogin}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3 min-[440px]:justify-end">
              <span className="text-xs text-muted-foreground">
                Linked {formatDateLong(account.createdAt)}
              </span>
              <ConfirmDialog
                title="Unlink this provider?"
                description={
                  <>
                    Unlink <strong>{label}</strong> from this user. This removes the OAuth
                    connection and its sign-in identity. If it is their only provider they will be
                    unable to sign in until they link a new one.
                  </>
                }
                confirmLabel="Unlink"
                onConfirm={() => unlink(account.provider)}
                disabled={unlinkMutation.isPending}
                trigger={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    data-testid={`unlink-provider-${account.provider}`}
                    aria-label={`Unlink ${label}`}
                    disabled={unlinkMutation.isPending}
                  >
                    <Unlink className="icon-sm" />
                  </Button>
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// The membership shape as returned by the typed admin client. (The generated
// `Organization` type differs slightly from the shared one — e.g.
// `emailDomains` widens to `unknown[]` — so derive the prop type from the
// facade rather than coupling to the shared `AuthMembership`.)
type AdminOrgMembership = NonNullable<
  ReturnType<typeof useAdminUserOrganizations>['data']
>['organizations'][number];

interface OrganizationsSectionProps {
  memberships: AdminOrgMembership[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

function OrganizationsSection({ memberships, isLoading, isError }: OrganizationsSectionProps) {
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState message="Failed to load organizations." />;
  if (!memberships || memberships.length === 0) {
    return (
      <EmptyState
        variant="bordered"
        icon={<Building2 className="h-8 w-8" />}
        title="No organization memberships"
        description="This user does not belong to any organizations."
      />
    );
  }

  // Sort personal orgs last so shared orgs surface first; within each group
  // sort alphabetically for a stable ordering across renders.
  const sorted = [...memberships].sort((a, b) => {
    if (a.organization.isPersonal !== b.organization.isPersonal) {
      return a.organization.isPersonal ? 1 : -1;
    }
    return a.organization.name.localeCompare(b.organization.name);
  });

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((membership) => {
        const org = membership.organization;
        return (
          <Link
            key={membership.memberId}
            to={ROUTES.admin.organization(org.id)}
            data-testid={`user-organization-${org.id}`}
            aria-label={`View organization ${org.name}`}
            className="flex flex-col gap-3 rounded-lg border p-3 transition-opacity hover:opacity-80 min-[440px]:flex-row min-[440px]:items-center min-[440px]:justify-between"
          >
            <div className="flex min-w-0 items-center gap-3">
              <UserAvatar
                userName={org.name}
                avatarUrl={org.avatarUrl ?? undefined}
                size="sm"
                className="shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="truncate font-medium">{org.name}</span>
                  {org.isPersonal ? (
                    <Badge variant="outline" className="text-3xs">
                      Personal
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-3xs">
                      Shared
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                  {org.slug}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-1 min-[440px]:justify-end">
              {membership.roles.length === 0 ? (
                <span className="text-xs italic text-muted-foreground">No roles</span>
              ) : (
                membership.roles.map((role) => (
                  <Badge key={role.id} variant="outline" className="text-3xs">
                    {role.name}
                  </Badge>
                ))
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

interface PlatformAccessSectionProps {
  userId: string;
  /** Whether the target user holds `users.is_super_admin`. */
  targetIsSuperAdmin: boolean;
  /** Whether the current viewer can mutate platform-admin assignments. */
  viewerCanManage: boolean;
}

/**
 * Card that surfaces the user's instance-wide admin status:
 *
 * - The literal `users.is_super_admin` flag (read-only — bootstrap /
 *   break-glass only, never mutable through the UI).
 * - The assignable platform-admin role (toggleable via the
 *   `/admin/app-admins` endpoint).
 *
 * Both confer the same server-side bypass; the difference is
 * provenance. The toggle is hidden until the app-admin list resolves
 * to avoid flashing an incorrect default state.
 */
function PlatformAccessSection({
  userId,
  targetIsSuperAdmin,
  viewerCanManage,
}: PlatformAccessSectionProps) {
  const appAdminsQuery = useAppAdmins();
  const isAppAdmin = !!appAdminsQuery.data?.admins.some((entry) => entry.user.id === userId);
  const [pending, setPending] = useState(false);

  // The facade hooks invalidate the app-admins list on success; the per-call
  // callbacks below add the page's toasts and reset the pending state.
  const grant = useGrantAppAdminMutation();
  const revoke = useRevokeAppAdminMutation();

  const setRole = (next: boolean) => {
    setPending(true);
    if (next) {
      grant.mutate(
        { data: { userId } },
        {
          onSuccess: () => toast.success('Platform admin role granted'),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to grant platform admin role'),
          onSettled: () => setPending(false),
        },
      );
    } else {
      revoke.mutate(
        { userId },
        {
          onSuccess: () => toast.success('Platform admin role revoked'),
          onError: (err) =>
            toast.error(
              err instanceof Error ? err.message : 'Failed to revoke platform admin role',
            ),
          onSettled: () => setPending(false),
        },
      );
    }
  };

  return (
    <SectionCard
      title="Platform access"
      description="Instance-wide admin privileges. Both flags below bypass every per-org permission check on the server."
      icon={<ShieldUser />}
      contentClassName="space-y-4"
    >
      <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Super admin</span>
            {targetIsSuperAdmin ? (
              <Badge variant="outline" className="gap-1 text-3xs">
                <ShieldCheck className="h-3 w-3 text-status-success" />
                Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="text-3xs text-muted-foreground">
                No
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            The literal <code className="font-mono">users.is_super_admin</code> flag. Only set by
            the setup wizard or break-glass scripts, not mutable from the UI.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Platform admin role</span>
            {appAdminsQuery.isLoading ? (
              <Badge variant="outline" className="text-3xs text-muted-foreground">
                Loading…
              </Badge>
            ) : isAppAdmin ? (
              <Badge variant="outline" className="gap-1 text-3xs">
                <ShieldCheck className="h-3 w-3 text-status-success" />
                Assigned
              </Badge>
            ) : (
              <Badge variant="outline" className="text-3xs text-muted-foreground">
                Not assigned
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Grants the same bypass as super admin, but assignable via the UI. Holders can manage
            every organization on this instance.
          </p>
        </div>
        <Switch
          data-testid="platform-admin-toggle"
          aria-label="Toggle platform admin role"
          disabled={appAdminsQuery.isLoading || pending || !viewerCanManage}
          checked={isAppAdmin}
          onCheckedChange={setRole}
        />
      </div>
    </SectionCard>
  );
}

interface AccountStatusSectionProps {
  userId: string;
  erased: boolean;
  targetStatus: UserLifecycleStatus;
  targetIsSuperAdmin: boolean;
  /** Whether the current viewer is also the target — blocks self-disable. */
  isSelf: boolean;
  /** Whether the current viewer can mutate account-status state. */
  viewerCanManage: boolean;
}

/**
 * Card that toggles `users.disabled`. A disabled user is blocked at the
 * server's auth middleware with HTTP 403 `account_disabled` on every
 * authenticated API call, and the web client renders a contact-support
 * screen instead of mounting the app shell. Sessions are revoked
 * server-side at the moment of toggle.
 *
 * The toggle is locked when the target is the viewer (no self-disable)
 * or a platform admin (refused server-side; surfaced as a disabled
 * control with a helper line so admins know why).
 */
function AccountStatusSection({
  userId,
  erased,
  targetStatus,
  targetIsSuperAdmin,
  isSelf,
  viewerCanManage,
}: AccountStatusSectionProps) {
  const queryClient = useQueryClient();
  const appAdminsQuery = useAppAdmins();
  const targetIsAppAdmin = !!appAdminsQuery.data?.admins.some((entry) => entry.user.id === userId);
  const targetIsPlatformAdmin = targetIsSuperAdmin || targetIsAppAdmin;
  const [pending, setPending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const targetDisabled = targetStatus === 'disabled';
  // A pre-provisioned account has no claimed identity yet, so there is
  // nothing to enable or disable: it becomes `active` on first verified
  // sign-in and is released by deleting the org or removing the member.
  // The server refuses a manual status change on it (400
  // `account_pending`), so the toggle is locked here rather than
  // offering an action that fails.
  const targetPending = targetStatus === 'pending';

  // The facade invalidates the app-admins list; the user detail + admin users
  // list are web-owned query keys, so invalidate them in the per-call callback.
  const toggle = useSetAdminUserStatusMutation();
  const setStatus = (next: AccountStatus) => {
    setPending(true);
    toggle.mutate(
      { userId, data: { status: next } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
          toast.success(next === 'disabled' ? 'Account disabled' : 'Account re-enabled');
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to update account status'),
        onSettled: () => setPending(false),
      },
    );
  };

  const lockReason = erased
    ? 'This account has been permanently erased and cannot be re-enabled.'
    : targetPending
      ? 'This account has not been claimed yet. It becomes active automatically when its owner first signs in.'
      : isSelf
        ? 'You cannot disable your own account.'
        : targetIsPlatformAdmin && !targetDisabled
          ? 'Revoke this user’s platform-admin role before disabling their account.'
          : null;

  const disableSwitch =
    appAdminsQuery.isLoading ||
    pending ||
    !viewerCanManage ||
    isSelf ||
    targetPending ||
    (targetIsPlatformAdmin && !targetDisabled);

  return (
    <SectionCard
      title="Account status"
      description="Disabled users see a contact-support screen on every page and cannot use the API. Sessions are revoked at the moment of disabling."
      icon={<Ban />}
      variant={targetDisabled ? 'destructive' : undefined}
      contentClassName="space-y-4"
    >
      <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">Disabled</span>
            {targetDisabled ? (
              <Badge variant="destructive" className="gap-1 text-3xs">
                <Ban className="h-3 w-3" />
                Yes
              </Badge>
            ) : (
              <Badge variant="outline" className="text-3xs text-muted-foreground">
                No
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {lockReason ??
              'Toggle off to restore access. The user will need to sign in again: their previous sessions were revoked.'}
          </p>
        </div>
        <Switch
          data-testid="account-disabled-toggle"
          aria-label="Toggle account disabled"
          disabled={disableSwitch}
          checked={targetDisabled}
          onCheckedChange={(next) => {
            if (next) {
              setConfirmOpen(true);
            } else {
              setStatus('active');
            }
          }}
        />
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable this account?</AlertDialogTitle>
            <AlertDialogDescription>
              The user will be signed out of every session immediately and will see a
              contact-support screen on every page until you re-enable them. This does not delete
              any data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setStatus('disabled')}>
              Disable account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const userQuery = useUser(userId);
  const providersQuery = useUserProviders(userId);
  const { isPlatformAdmin, user: currentUser } = useAuth();
  const { adminUserActions } = usePlugins();
  const erasureQuery = useAdminUserErasure(userId ?? '', isPlatformAdmin);
  const erased = !!erasureQuery.data;
  const canManageAccount = isPlatformAdmin && erasureQuery.isSuccess && !erased;
  const organizationsQuery = useAdminUserOrganizations(userId ?? '');

  const backAction = (
    <Button asChild variant="outline" size="sm">
      <Link to={ROUTES.admin.users}>Back to users</Link>
    </Button>
  );

  if (userQuery.isLoading) {
    return (
      <AdminSubPageLayout title="User" actions={backAction}>
        <LoadingState />
      </AdminSubPageLayout>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <AdminSubPageLayout title="User" actions={backAction}>
        <EmptyState
          variant="bordered"
          icon={<UserIcon className="h-8 w-8" />}
          title="User not found"
          description="This user may have been removed or you do not have access."
          action={
            <Button asChild variant="link">
              <Link to={ROUTES.admin.users}>Back to users</Link>
            </Button>
          }
        />
      </AdminSubPageLayout>
    );
  }

  const user = userQuery.data;
  const displayName = getUserDisplayName(user);

  const headerActions = (
    <div className="flex items-center gap-2">
      {!erased && adminUserActions.length > 0 && (
        <Suspense fallback={null}>
          {adminUserActions.map(({ id, pluginName, component: Component }) => (
            <Component key={`${pluginName}:${id}`} user={user} variant="detail" />
          ))}
        </Suspense>
      )}
      {backAction}
    </div>
  );

  return (
    <AdminSubPageLayout title={displayName} description={`@${user.login}`} actions={headerActions}>
      <SectionCard title="Identity" icon={<UserIcon />} contentClassName="space-y-4">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <UserAvatar
            userName={displayName}
            avatarUrl={user.avatarUrl ?? undefined}
            size="md"
            className="shrink-0"
          />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-base font-medium">{displayName}</p>
              {erased && <Badge variant="destructive">Erased</Badge>}
              {!erased && user.status === 'disabled' && (
                <Badge variant="destructive" className="gap-1 text-3xs">
                  <Ban className="h-3 w-3" />
                  Disabled
                </Badge>
              )}
              {user.status === 'pending' && (
                <Badge variant="outline" className="gap-1 text-3xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Pending
                </Badge>
              )}
            </div>
            <p className="truncate font-mono text-xs text-muted-foreground">@{user.login}</p>
            {user.email && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <SectionLabel as="dt">User ID</SectionLabel>
            <dd className="mt-0.5 truncate font-mono text-xs">{user.id}</dd>
          </div>
          <div>
            <SectionLabel as="dt">Joined</SectionLabel>
            <dd className="mt-0.5">{formatDateLong(user.createdAt)}</dd>
          </div>
          <div>
            <SectionLabel as="dt">Primary provider</SectionLabel>
            <dd className="mt-0.5">
              <Badge variant="outline">{getProviderLabel(user.providerId)}</Badge>
            </dd>
          </div>
          <div>
            <SectionLabel as="dt">Email</SectionLabel>
            <dd className="mt-0.5 flex flex-wrap items-center gap-2">
              {user.emailVerified ? (
                <Badge variant="outline" className="gap-1">
                  <ShieldCheck className="h-3 w-3 text-status-success" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  Unverified
                </Badge>
              )}
            </dd>
          </div>
        </dl>
      </SectionCard>

      <PlatformAccessSection
        userId={user.id}
        targetIsSuperAdmin={user.isSuperAdmin === true}
        viewerCanManage={canManageAccount}
      />

      <AccountStatusSection
        erased={erased}
        userId={user.id}
        targetStatus={user.status ?? 'active'}
        targetIsSuperAdmin={user.isSuperAdmin === true}
        isSelf={currentUser?.id === user.id}
        viewerCanManage={canManageAccount}
      />

      {isPlatformAdmin && (
        <UserErasureSection key={user.id} user={user} isSelf={currentUser?.id === user.id} />
      )}

      <SectionCard
        title="Organizations"
        description="Every organization this user is a member of."
        icon={<Building2 />}
      >
        <OrganizationsSection
          memberships={organizationsQuery.data?.organizations}
          isLoading={organizationsQuery.isLoading}
          isError={organizationsQuery.isError}
        />
      </SectionCard>

      <SectionCard
        title="Linked OAuth providers"
        description="Every OAuth provider this user has connected to their account."
      >
        <ProvidersSection
          userId={userId ?? ''}
          providers={providersQuery.data}
          isLoading={providersQuery.isLoading}
          isError={providersQuery.isError}
        />
      </SectionCard>
    </AdminSubPageLayout>
  );
}
